import React, { useEffect, useState } from 'react';
import { IImage } from 'tbk-components/src/components/Image';
import LocationSubpageHero from '../LocationSubpageHero';
import Spacer from '../../common/Spacer';
import { ILink } from 'tbk-components/src/components/Button';

/**
 * @useTermData
 * @taxonomy location department source_ats job_type business_unit
 */
export interface ICareersLocation {
	/**
	 * These are just placeholder values.  Please click "Link to Term Data" below and select the location
	 * you want to showcase here.
	 */
	name: string;
	/**
	 * @noUI
	 */
	term_id: number;
	/**
	 * @noUI
	 */
	taxonomy: string;
	/**
	 * @noUI
	 */
	slug: string;
	numberOfCareers: number;
	numberOfCategories: number;
}

export interface ICareersLocationHeroProps {
	/**
	 * Select the location to display the hero for.  This will be used to replace placeholders in the tagline and
	 * heading.
	 */
	location: ICareersLocation[];
	/**
	 * This is meant to display something like "16 Careers in 3 Categories".  Use the placeholder %1 for the
	 * number of careers and %2 for the number of categories.  We will fill those in automatically based on the
	 * location you've select.  For example, "%1 Careers in %2 Categories".
	 *
	 * Note, we are removing this and filling it in automatically server side to accommodate singular/plural variations.
	 *
	 * @noUI
	 */
	tagline?: string;
	/**
	 * This is meant to be something like "Welcome to Saskatoon".  Use the placeholder %1 for the location name.
	 * For example, "Welcome to %1".  If you have multiple locations selected, we will combine them together with an &.
	 */
	heading?: string;
	/**
	 * First of three images to display in the hero.
	 */
	imageOne?: IImage;
	/**
	 * Second of three images to display in the hero.
	 */
	imageTwo?: IImage;
	/**
	 * Third of three images to display in the hero.
	 */
	imageThree?: IImage;
	/**
	 * The CTA button.  Keep the URL as #calian-career-group to link to the careers list.
	 */
	ctaButton?: ILink;
	/**
	 * Enable Desktop Picture Animations
	 */
	enableDesktopPictureAnimation?: boolean;
	/**
	 * Desktop Picture Animation Speed in Milliseconds
	 */
	desktopPictureAnimationSpeed?: number;
	/**
	 * Enable Tablet / Mobile Picture Animations
	 */
	enableMobilePictureAnimation?: boolean;
	/**
	 * Tablet / Mobile Picture Animation Speed in Milliseconds
	 */
	mobilePictureAnimationSpeed?: number;
}

/**
 * This component is meant to be used on the landing page for a specific location.  It should be the first component
 * on the page and will display a hero with a tagline, heading, and up to three images.
 *
 * @block
 * @icon superhero
 */
const CareersLocationHero: React.FC<ICareersLocationHeroProps> = ({
	location,
	tagline = '%1 jobs in %2 categories',
	heading = 'Welcome to %1',
	imageOne,
	imageTwo,
	imageThree,
	ctaButton,
	enableDesktopPictureAnimation = true,
	desktopPictureAnimationSpeed = 15,
	enableMobilePictureAnimation = true,
	mobilePictureAnimationSpeed = 15,
}) => {
	const [filledTagline, setFilledTagline] = useState<string>(null);
	const [filledHeading, setFilledHeading] = useState<string>(null);

	useEffect(() => {
		if ( ! location ) {
			return;
		}
		const data = {} as ICareersLocation;
		const names = [];
		( Array.isArray( location ) ? location : [location as ICareersLocation] ).forEach( l => {
			['numberOfCareers','numberOfCategories'].forEach( key => {
				if ( ! data.hasOwnProperty( key ) ) {
					data[ key ] = 0;
				}
				data[ key ] += parseInt( l[ key ], 10 );
			});
			names.push( l.name );
		});
		const fillTagline = (data:ICareersLocation) => {
			setFilledTagline( ( tagline || '' )
				.replace('%1', `<strong>${data.numberOfCareers}</strong>`)
				.replace('%2', `<strong>${data.numberOfCategories}</strong>`)
			);
		};
		setFilledHeading( ( heading || '' ).replace('%1', names.map( n => `<strong>${n}</strong>` ).join( ' & ' ) ) );
		if ( data.hasOwnProperty('numberOfCareers') && data.hasOwnProperty('numberOfCategories') ) {
			fillTagline( data );
		}
	}, [location, tagline, heading]);

	return (
		<>
			<LocationSubpageHero
				tagline={filledTagline}
				heading={filledHeading}
				featureImageOne={imageOne || null}
				featureImageTwo={imageTwo || null}
				featureImageThree={imageThree || null}
				ctaButton={ctaButton || null}
				enableDesktopPictureAnimation={enableDesktopPictureAnimation}
				desktopPictureAnimationSpeed={desktopPictureAnimationSpeed}
				enableMobilePictureAnimation={enableMobilePictureAnimation}
				mobilePictureAnimationSpeed={mobilePictureAnimationSpeed}
			/>
			<Spacer size="Large on Desktop" />
		</>
	);
};

export default CareersLocationHero;
